import { LOCALE_UI, type UiLang } from '@ai-job-fit/engine/langues'
// Les Réglages de Radar se cochent À L'ENDROIT (issue #173) : « Je parle
// français, anglais » plutôt que « Langues exigées que je ne parle pas : NL DE ».
// La base, elle, garde le sens d'origine (`exclusions`, cf.
// src/services/criteresOffre.ts) : c'est ce que lisent la nuit, les mails et
// l'accueil, et un réglage déjà posé ne doit pas changer de sens en route.
//
// Ce module ne fait que la conversion, dans les deux sens. Pur, pour être
// testé sans navigateur.

/** Dans l'ordre où l'écran les montre. */
export const LANGUES_PARLEES = ['fr', 'nl', 'en', 'de'] as const
export type LangueParlee = (typeof LANGUES_PARLEES)[number]

/** Les langues cochées « Je parle » : toutes, sauf celles que l'offre ne doit
 *  pas exiger. Aucune exclusion vaut donc « je parle tout », l'état de départ. */
export function languesParlees(exclues: readonly string[]): LangueParlee[] {
  return LANGUES_PARLEES.filter((l) => !exclues.includes(l))
}

/** Le chemin inverse : ce qui n'est pas coché devient une langue exclue. */
export function languesExclues(parlees: readonly string[]): LangueParlee[] {
  return LANGUES_PARLEES.filter((l) => !parlees.includes(l))
}

/**
 * Cocher ou décocher une langue. On ne décoche jamais la dernière : « je ne
 * parle aucune langue » écarterait toute offre qui en exige une, c'est-à-dire
 * presque toutes, sans que personne l'ait voulu.
 */
export function basculerLangue(parlees: readonly LangueParlee[], l: LangueParlee): LangueParlee[] {
  return basculer(LANGUES_PARLEES, parlees, l)
}

// ---------------------------------------------------------------------------
// Contrats et niveaux (issue #174) : « J'accepte CDI, CDD » plutôt que « Je
// refuse l'intérim ». Même mécanique que les langues : ce qui n'est pas coché
// est exclu, rien d'exclu vaut tout coché, et le dernier choix ne se décoche
// pas (« aucun contrat » n'aurait laissé passer que les offres muettes).

export const CONTRATS_ACCEPTES = ['cdi', 'cdd', 'interim', 'freelance', 'stage'] as const
export const NIVEAUX_ACCEPTES = ['junior', 'confirme', 'senior'] as const

/** Ce qui est coché à l'écran : `ordre` moins ce qui est exclu en base. */
export function coches<T extends string>(ordre: readonly T[], exclus: readonly string[]): T[] {
  return ordre.filter((x) => !exclus.includes(x))
}

/** Ce qui part en base : `ordre` moins ce qui est coché. */
export function exclus<T extends string>(ordre: readonly T[], coche: readonly string[]): T[] {
  return ordre.filter((x) => !coche.includes(x))
}

/** Cocher ou décocher `v`, sans jamais décocher le dernier. */
export function basculer<T extends string>(ordre: readonly T[], coche: readonly T[], v: T): T[] {
  if (coche.includes(v)) return coche.length > 1 ? coche.filter((x) => x !== v) : [...coche]
  return ordre.filter((x) => x === v || coche.includes(x))
}

// ---------------------------------------------------------------------------
// Salaire minimum en paliers (issue #174)

export const PALIERS_SALAIRE = [40000, 50000, 60000] as const

/** Le palier coché pour un montant en base : `null` (aucun), un palier, ou
 *  `autre` pour un montant libre. */
export function palierDe(montant: number | null): number | null | 'autre' {
  if (!montant) return null
  return (PALIERS_SALAIRE as readonly number[]).includes(montant) ? montant : 'autre'
}

/** « 45 000 », séparateur de milliers de la langue de l'écran. */
export function formaterMontant(n: number, lang: UiLang): string {
  return new Intl.NumberFormat(LOCALE_UI[lang], { maximumFractionDigits: 0 }).format(n)
}

/**
 * Lit ce qu'on tape dans « Autre… » : « 45000 », « 45 000 », « 45.000 »,
 * « 45k » ou « 45 k€ ». `null` pour vide ou illisible. Un nombre sous 1 000
 * est lu en milliers : personne ne cherche un poste à 45 € par an.
 */
export function lireMontant(texte: string): number | null {
  const t = texte.trim().toLowerCase().replace(/€|eur/g, '')
  if (!t) return null
  const k = /k\s*$/.test(t)
  const chiffres = t.replace(/k\s*$/, '').replace(/[\s.,\u00a0\u202f']/g, '')
  if (!/^\d+$/.test(chiffres)) return null
  let n = Number(chiffres)
  if (k || n < 1000) n *= 1000
  return n > 0 ? n : null
}
